import { useEffect } from 'react';

// Escape closes the toast that has focus, and not while a dialog is up
// (modal, phone drawer, date popover). Don't stop the event. Those listen
// for Escape too, and a capture listener here would beat them.
const ToastBehavior = () => {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' && event.key !== 'Esc') return;

            const active = document.activeElement;
            if (!(active instanceof Element)) return;
            const container = active.closest('.Toastify__toast-container');
            if (!container) return;

            const dialogs = document.querySelectorAll('[role="dialog"]');
            for (let i = 0; i < dialogs.length; i++) {
                const dialog = dialogs[i];
                if (!(dialog instanceof HTMLElement)) continue;
                if (dialog.closest('.Toastify')) continue;
                if (dialog.getAttribute('aria-hidden') === 'true') continue;
                const style = window.getComputedStyle(dialog);
                if (style.display === 'none' || style.visibility === 'hidden') continue;
                if (dialog.getClientRects().length === 0) continue;
                return;
            }

            const toasts = container.querySelectorAll('.Toastify__toast');
            let newest: Element | null = null;
            for (let i = 0; i < toasts.length; i++) {
                const node = toasts[i];
                // Still in the DOM for the exit animation. Closing it again dismisses the rest.
                if (/__(?:bounce|slide|zoom|flip)-exit/.test(node.className)) continue;
                newest = node;
            }
            if (!newest) return;

            const closeButton = newest.querySelector<HTMLButtonElement>('.app-toast-close, .Toastify__close-button');
            closeButton?.click();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    // Sit above the view-order bar and the check-in notice. Both are fixed to the bottom.
    useEffect(() => {
        let frame = 0;
        const place = () => {
            let lift = 0;
            const bars = document.querySelectorAll('.view-order-bar.show, .check-in-notice');
            for (let i = 0; i < bars.length; i++) {
                const bar = bars[i];
                if (bar instanceof HTMLElement && bar.offsetHeight > lift) lift = bar.offsetHeight;
            }
            document.documentElement.style.setProperty('--app-toast-lift', `${lift}px`);
        };
        const schedule = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                place();
            });
        };

        const isBar = (node: Node) => {
            if (!(node instanceof Element)) return false;
            return (
                node.matches('.view-order-bar, .check-in-notice') ||
                node.querySelector('.view-order-bar, .check-in-notice') !== null
            );
        };

        schedule();
        const observer = new MutationObserver((mutations) => {
            const relevant = mutations.some((mutation) => {
                if (mutation.type === 'childList') {
                    const nodes = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes));
                    return nodes.some(isBar);
                }
                return (
                    mutation.target instanceof Element &&
                    mutation.target.closest('.view-order-bar, .check-in-notice') !== null
                );
            });
            if (relevant) schedule();
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
        });
        window.addEventListener('resize', schedule);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', schedule);
            if (frame) window.cancelAnimationFrame(frame);
            document.documentElement.style.removeProperty('--app-toast-lift');
        };
    }, []);

    return null;
};

export default ToastBehavior;
